import { type Flags, useSendFeedback } from '@reflag/react-sdk';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { atomFamily, useRecoilState } from 'recoil';

import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    type ButtonProps,
    Description,
    FieldError,
    Form,
    Label,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    Submit,
    TextField,
    TextInput,
} from '@gitbook/spine-react';

import { persistRecoilEffect } from '../../utils';

export type RequestFeatureProps = {
    title?: string;
    description?: string;
    flagKey: keyof Flags;
};

type RequestFeatureFormValues = {
    message: string;
};

const submittedFeatureRequestsAtom = atomFamily<boolean, keyof Flags>({
    key: 'submittedFeatureRequestsAtom',
    default: false,
    effects: [persistRecoilEffect()],
});

/**
 * Button to request access to a feature.
 */
export function RequestFeatureButton(props: RequestFeatureProps & Partial<ButtonProps>) {
    const { title, description, flagKey, ...rest } = props;
    const [submitted, setSubmitted] = useRecoilState(submittedFeatureRequestsAtom(flagKey));

    return (
        <PopoverRoot
            onOpenChange={(opened) => {
                if (opened) {
                    analytics.logEvent({
                        type: 'feature_request_access_open',
                        featureKey: flagKey,
                    });
                }
            }}
        >
            <PopoverTrigger>
                <Button
                    kind="secondary"
                    leadingIcon={submitted ? Icon.Check : Icon.Bell}
                    label={submitted ? 'Request sent' : 'Get early access'}
                    {...rest}
                />
            </PopoverTrigger>
            <Popover className="p-4">
                <RequestFeatureForm
                    title={title}
                    description={description}
                    flagKey={flagKey}
                    submitted={submitted}
                    onSubmitDone={() => setSubmitted(true)}
                />
            </Popover>
        </PopoverRoot>
    );
}

function RequestFeatureForm(
    props: RequestFeatureProps & {
        submitted: boolean;
        onSubmitDone: () => void;
    }
) {
    const { title, description, flagKey, submitted, onSubmitDone } = props;

    const sendFeedback = useSendFeedback();

    const form = useForm<RequestFeatureFormValues>({
        values: {
            message: '',
        },
    });

    const onSubmit: SubmitHandler<RequestFeatureFormValues> = async ({ message }) => {
        await sendFeedback({
            flagKey,
            comment: message,
        });

        analytics.logEvent({
            type: 'feature_request_access_submit',
            featureKey: flagKey,
        });

        onSubmitDone();
    };

    return (
        <div>
            {submitted ? (
                <div className="flex flex-col gap-2">
                    <Description>
                        Your request has been sent. We'll get back to you soon.
                    </Description>
                </div>
            ) : (
                <Form
                    form={form}
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="flex w-80 flex-col gap-2"
                >
                    <TextField name="message" required minLength={1} maxLength={400}>
                        <Label>{title ?? 'Tell us more about your use-case'}</Label>
                        {description ? <Description>{description}</Description> : null}
                        <TextInput autoFocus placeholder="Tell us more." multiline />
                        <FieldError />
                    </TextField>
                    <div className="flex justify-end">
                        <Submit label="Submit" />
                    </div>
                </Form>
            )}
        </div>
    );
}
